import { chakra } from '@chakra-ui/react';
import { type ReactNode } from 'react';

const Root = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6',
    width: 'full',
    minWidth: 0,
    // Room so the sticky action bar never sits over the last section's content.
    pb: '2',
  },
});

const Column = chakra('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5',
    width: 'full',
    minWidth: 0,
    mx: 'auto',
  },
});

const Bar = chakra('div', {
  base: {
    position: 'sticky',
    bottom: '0',
    zIndex: 'sticky',
    width: 'full',
    minWidth: 0,
    mx: 'auto',
    mt: '2',
    display: 'flex',
    flexDirection: { base: 'column', md: 'row' },
    alignItems: { base: 'stretch', md: 'center' },
    justifyContent: 'space-between',
    gap: '3',
    px: { base: '4', md: '5' },
    py: '3',
    bg: 'surface.raised',
    borderWidth: '1px',
    borderColor: 'border.default',
    borderRadius: 'xl',
    boxShadow: 'md',
  },
});

export interface FocusLayoutProps {
  /** Contextual header (PageHeader) — scrolls normally above the sections. */
  header?: ReactNode;
  /** The focused, sectioned workspace body (a Stack of Panels/Disclosures). */
  children: ReactNode;
  /**
   * A persistent summary + primary action bar. Sticks to the bottom of the viewport so
   * the primary action (Publish) is always reachable while the operator scrolls the
   * sections. Stacks vertically on mobile so the action stays ≥44px and full-width.
   */
  actionBar?: ReactNode;
  /** Max width of the focused column (a task mode is legible, not full-bleed). */
  maxWidth?: string;
  'data-testid'?: string;
}

/**
 * Focused task-mode scaffold (§ composition — "Agent builder: focused task mode — a
 * distraction-reduced, sectioned workspace with a persistent summary + sticky primary
 * action"). A single centred, width-capped column of sections with a header above and a
 * sticky summary/action bar pinned to the bottom. No split panes, no permanent sidebar —
 * one calm column that keeps the primary action in reach.
 */
export function FocusLayout({
  header,
  children,
  actionBar,
  maxWidth = '60rem',
  ...rest
}: FocusLayoutProps) {
  return (
    <Root {...rest}>
      {header}
      <Column maxW={maxWidth}>{children}</Column>
      {actionBar && <Bar maxW={maxWidth}>{actionBar}</Bar>}
    </Root>
  );
}
